/**
 * NUEVO: frontend/src/pages/LandingEditor.js
 * Agregar en App.js: <Route path="/admin/landing" element={<AdminGuard><LandingEditor /></AdminGuard>} />
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { Navbar, Btn, C } from "../components/UI";

export default function LandingEditor() {
  const nav = useNavigate();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [tab, setTab] = useState("visual"); // visual | ai | json
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiFetch("/landing").then(setContent).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch("/landing", { method:"PUT", body:JSON.stringify(content) });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const generateWithAI = async () => {
    if (!aiPrompt.trim()) return;
    setGenerating(true);
    try {
      const res = await fetch("/api/landing/generate", {
        method:"POST",
        headers:{ "Content-Type":"application/json", "Authorization":`Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ prompt: aiPrompt, current: content })
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const text = decoder.decode(value);
        const lines = text.split("\n").filter(l => l.startsWith("data: "));
        for (const line of lines) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.content) { setContent(data.content); setAiPrompt(""); }
            if (data.error) alert(data.error);
          } catch {}
        }
      }
    } catch(e) { alert(e.message); }
    finally { setGenerating(false); }
  };

  const setPath = (path, value) => {
    const keys = path.split(".");
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length-1]] = value;
      return next;
    });
  };

  if (!content) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ color:C.muted }}>Cargando…</div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column" }}>
      <Navbar />
      <div style={{ maxWidth:"1000px", margin:"0 auto", padding:"2rem 1.5rem", width:"100%" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", marginBottom:"2rem" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:"1.2rem" }}>Editor de Landing</div>
            <div style={{ color:C.muted, fontSize:"0.82rem" }}>Los cambios se reflejan en tiempo real en la página principal</div>
          </div>
          <div style={{ marginLeft:"auto", display:"flex", gap:"10px" }}>
            <Btn variant="ghost" onClick={() => nav("/admin")}>← Admin</Btn>
            <Btn onClick={save} loading={saving} variant={saved?"success":"primary"}>
              {saved ? "✓ Guardado" : "Guardar cambios"}
            </Btn>
            <Btn variant="ghost" onClick={() => window.open("/", "_blank")}>Ver landing ↗</Btn>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, marginBottom:"1.5rem" }}>
          {[["visual","✏️ Editor visual"],["ai","🤖 Generar con IA"],["json","{ } JSON directo"]].map(([id,label]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ background:"none", border:"none", color:tab===id?C.accent:C.muted,
                fontFamily:"'Syne',sans-serif", fontWeight:tab===id?600:400, fontSize:"0.83rem",
                padding:"9px 18px", borderBottom:tab===id?`2px solid ${C.accent}`:"2px solid transparent",
                marginBottom:"-1px", cursor:"pointer" }}>{label}</button>
          ))}
        </div>

        {/* Visual editor */}
        {tab === "visual" && (
          <div style={{ display:"flex", flexDirection:"column", gap:"1.5rem" }}>

            {/* Hero */}
            <Section title="Hero">
              {[
                ["hero.badge","Badge (texto pequeño arriba)"],
                ["hero.title","Título principal"],
                ["hero.highlight","Parte destacada (en amarillo)"],
                ["hero.subtitle","Subtítulo"],
                ["hero.cta_primary","Botón primario"],
                ["hero.cta_secondary","Botón secundario"],
              ].map(([path, label]) => (
                <Field key={path} label={label} value={path.split(".").reduce((o,k)=>o?.[k], content) || ""}
                  onChange={v => setPath(path, v)} />
              ))}
            </Section>

            {/* Navbar */}
            <Section title="Navbar">
              <Field label="Nombre de la marca" value={content.navbar?.brand || ""} onChange={v => setPath("navbar.brand", v)} />
              <Field label="Texto del botón" value={content.navbar?.cta || ""} onChange={v => setPath("navbar.cta", v)} />
            </Section>

            {/* Features */}
            <Section title={`Features (${content.features?.length || 0})`}>
              {content.features?.map((f, i) => (
                <div key={i} style={{ background:"#0d0d0f", borderRadius:"8px", padding:"1rem", marginBottom:"8px" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"60px 1fr 2fr", gap:"8px", alignItems:"center" }}>
                    <input value={f.icon} onChange={e => {
                      const features = [...content.features];
                      features[i] = {...features[i], icon: e.target.value};
                      setContent(p => ({...p, features}));
                    }} style={inputStyle} placeholder="🚀" />
                    <input value={f.title} onChange={e => {
                      const features = [...content.features];
                      features[i] = {...features[i], title: e.target.value};
                      setContent(p => ({...p, features}));
                    }} style={inputStyle} placeholder="Título" />
                    <input value={f.desc} onChange={e => {
                      const features = [...content.features];
                      features[i] = {...features[i], desc: e.target.value};
                      setContent(p => ({...p, features}));
                    }} style={inputStyle} placeholder="Descripción" />
                  </div>
                </div>
              ))}
            </Section>

            {/* Footer */}
            <Section title="Footer">
              <Field label="Texto del footer" value={content.footer?.text || ""} onChange={v => setPath("footer.text", v)} />
            </Section>
          </div>
        )}

        {/* AI generator */}
        {tab === "ai" && (
          <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
            <div style={{ background:"#0a1a0a", border:`1px solid ${C.success}33`, borderRadius:"12px", padding:"1.5rem" }}>
              <div style={{ fontWeight:600, marginBottom:"8px", display:"flex", gap:"8px", alignItems:"center" }}>
                🤖 Generar landing con Claude
              </div>
              <div style={{ color:C.muted, fontSize:"0.83rem", lineHeight:1.6, marginBottom:"1.25rem" }}>
                Describí tu producto y Claude va a generar toda la landing: título, features, pricing y más.
                Tu contenido actual se usa como base para mantener el estilo.
              </div>
              <div style={{ display:"flex", gap:"10px" }}>
                <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                  placeholder="Ej: Somos una plataforma de gestión de turnos para clínicas. El precio Pro es $49/mes. Nuestro diferencial es la integración con WhatsApp..."
                  rows={4}
                  style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, color:C.text,
                    borderRadius:"8px", padding:"12px", fontSize:"0.85rem", resize:"vertical",
                    fontFamily:"'Syne',sans-serif" }} />
              </div>
              <div style={{ marginTop:"10px", display:"flex", gap:"10px" }}>
                <Btn onClick={generateWithAI} loading={generating}>
                  {generating ? "Generando…" : "✨ Generar landing"}
                </Btn>
                {generating && <span style={{ alignSelf:"center", fontSize:"0.8rem", color:C.muted }}>
                  Esto tarda ~15 segundos…
                </span>}
              </div>
            </div>

            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:"12px", padding:"1.5rem" }}>
              <div style={{ fontWeight:600, marginBottom:"8px" }}>Ejemplos de prompts</div>
              {[
                "Somos una agencia de marketing digital. Plan básico $99/mes, Pro $299/mes, Enterprise consultar.",
                "Plataforma SaaS para restaurantes. Gestión de mesas, pedidos online y delivery. Plan único $49/mes.",
                "Herramienta de automatización para e-commerce. Integra con MercadoLibre y Shopify.",
              ].map((ex, i) => (
                <div key={i} onClick={() => setAiPrompt(ex)}
                  style={{ padding:"10px 14px", background:"#0d0d0f", borderRadius:"8px", marginBottom:"6px",
                    cursor:"pointer", fontSize:"0.82rem", color:C.muted, border:`1px solid transparent` }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = C.border}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "transparent"}>
                  {ex}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* JSON editor */}
        {tab === "json" && (
          <div>
            <div style={{ color:C.muted, fontSize:"0.82rem", marginBottom:"10px" }}>
              Editá el JSON directamente. Guardá para aplicar los cambios.
            </div>
            <textarea
              value={JSON.stringify(content, null, 2)}
              onChange={e => { try { setContent(JSON.parse(e.target.value)); } catch {} }}
              rows={30}
              style={{ width:"100%", background:"#0d0d0f", border:`1px solid ${C.border}`, color:C.text,
                borderRadius:"10px", padding:"16px", fontFamily:"'JetBrains Mono',monospace",
                fontSize:"0.8rem", lineHeight:1.6, resize:"vertical" }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:"12px", padding:"1.5rem" }}>
      <div style={{ fontWeight:600, marginBottom:"1rem", fontSize:"0.9rem" }}>{title}</div>
      <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>{children}</div>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"5px" }}>
      <label style={{ fontSize:"0.72rem", color:"#555", textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)} style={inputStyle} />
    </div>
  );
}

const inputStyle = {
  background:"#0d0d0f", border:`1px solid #1e1e26`, color:"#e8e6e1",
  borderRadius:"7px", padding:"8px 12px", fontSize:"0.85rem", width:"100%",
};
